import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, Link } from 'react-router-dom';
import { Search, Wallet, ExternalLink, Info, Edit2, MessageSquare, Bell, Award, TrendingUp, TrendingDown } from 'lucide-react';
import { doc, getDoc, collection, query, where, getDocs, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from './firebase';
import { onAccountsChanged, onChainChanged } from './walletService';
import { getEVMAddressData, getCoinSecurityLevel, getCoinHolders, getTokenHolders, getAddressTimestamps, getAddressSecurityLevel, getAddressSecurityLevels, getAddressIsContract } from './dataService';
import { getBitcoinAddressData, formatBitcoinBalance, calculateBitcoinTransactionTotals } from './blockchainService';
import { getLabel, formatAddressHumanReadable } from './labelService';
import WalletConnectModal from './WalletConnectModal';
import ProfileModal from './ProfileModal';
import SecurityFAQPage from './components/SecurityFAQPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import CommentSection from './CommentSection';
import CopyButton from './CopyButton';
import WebsiteHeader from './components/WebsiteHeader';
import SearchBox from './components/SearchBox';
import WebsiteFooter from './components/WebsiteFooter';
import { useAuth } from './hooks/useAuth';
import { useUniversalSearch } from './hooks/useUniversalSearch';
import { setAddressAlias, subscribeToAlias } from './aliasService';
import { getWatchedAddresses, addWatchedAddress, removeWatchedAddress, validateWatchAddress } from './userService';
import WatchButton from './components/WatchButton';
import { parseURLPath } from './urlUtils';
import { getPriceInUSD, batchGetTokenPrices, CHAIN_TO_COINGECKO_PLATFORM } from './priceService';
import { validateAddress, getChain, getExplorerUrl, normalizeChain, getNativeTokenCoinId } from './chainUtils';
import ChainBadge from './ChainBadge';
// import OrgVerificationBadge from './components/OrgVerificationBadge';
import UserBadges from './components/UserBadges';
import { trackAddressSearch, trackPageView, trackError, trackAliasCreate } from './analytics';
import { cleanupExpiredRateLimits, checkRateLimit, recordAction } from './rateLimitService';
import { normalizeAddress, formatAddress } from './addressUtils';
import { formatRelativeTime } from './utils/timeUtils';
import { validateAlias, ALIAS_MAX_LENGTH } from './utils/aliasValidation';
import SingleLineInput from './components/SingleLineInput';
import { getSecurityLevelInfo } from './utils/securityLevelUtils';
import { Shield } from 'lucide-react';
import CoinInfo from './components/CoinInfo';
import PopularCoins from './components/PopularCoins';
import TokenHolders from './components/TokenHolders';
import SafetyLevel from './components/SafetyLevel';
import Tooltip from './Tooltip';
import SEO, { generateCoinStructuredData, generateAddressStructuredData, generateWebsiteStructuredData } from './components/SEO';

// Lazy load email auth callback component
const EmailAuthCallback = lazy(() => import('./components/EmailAuthCallback'));

// Hard gate mode: when true, user must login to view coin/address pages
// When false, user can view pages but must login to perform write operations
const HARD_GATE_MODE = process.env.REACT_APP_HARD_GATE_MODE !== 'false';

// Component to display USD value for a funding source
// Uses pre-fetched prices from batch lookup when available
// Shows "..." while loading, "-" if price unavailable after load
const FundingSourceUSD = ({ source, nativePrice, tokenPrices }) => {
  const amount = parseFloat(source.amount) || 0;

  if (amount === 0) {
    return <span className="text-gray-400">-</span>;
  }

  let price = null;
  let isLoading = false;

  if (source.type === 'native') {
    // For native tokens (ETH), use the provided native price
    price = nativePrice;
    isLoading = nativePrice === null; // Still loading if null
  } else if (source.tokenAddress) {
    if (tokenPrices === null) {
      // Token prices haven't loaded yet
      isLoading = true;
    } else {
      // Token prices loaded - look up this token's price
    price = tokenPrices.get(source.tokenAddress.toLowerCase());
  }
  }

  // Show loading indicator while prices are being fetched
  if (isLoading) {
    return <span className="text-gray-400 animate-pulse">...</span>;
  }

  // Show dash if price not available after loading
  if (price == null) {
    return <span className="text-gray-400">-</span>;
  }

  const usdValue = amount * price;
  return (
    <span className="font-mono">${usdValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
  );
};

const CoinCommons = () => {
  const navigate = useNavigate();
  const { coinId, chain: urlChain, address: urlAddress } = useParams();

  // Use custom hooks
  const {
    user,
    userProfile,
    userOwnAlias,
    authInitialized,
    handleLogout,
    handleWalletConnectSuccess,
    setUser
  } = useAuth();

  const {
    searchAddress,
    setSearchAddress,
    searchResults,
    showSearchResults,
    setShowSearchResults,
    searchLoading,
    handleSearch: performSearch
  } = useUniversalSearch();

  // Initialize from URL params
  const [currentChain, setCurrentChain] = useState(urlChain || null);
  const [currentAddress, setCurrentAddress] = useState(urlAddress || null);
  const [addressData, setAddressData] = useState(null);
  const [coin, setCoin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSecurityFAQ, setShowSecurityFAQ] = useState(false);
  const [currentAddressAlias, setCurrentAddressAlias] = useState(null);
  const [showAddAliasInput, setShowAddAliasInput] = useState(false);
  const [newAliasValue, setNewAliasValue] = useState('');
  const [aliasError, setAliasError] = useState('');
  const [labelResult, setLabelResult] = useState(null);
  const [urlBasedAuth, setUrlBasedAuth] = useState(false);
  const [matchedCoin, setMatchedCoin] = useState(null);
  const [coinHoldersData, setCoinHoldersData] = useState(null);
  const [fundingSourcePrices, setFundingSourcePrices] = useState(null);
  const [fundingSourceSecurityLevels, setFundingSourceSecurityLevels] = useState(null);
  const [watchedAddresses, setWatchedAddresses] = useState([]);
  const [watchingAddress, setWatchingAddress] = useState(false);
  const [watchError, setWatchError] = useState('');

  // Helper to handle lazy loading errors - shows rate limit errors to user
  const handleLazyError = (error, context) => {
    const msg = error.message || '';
    // Check for Firebase rate limit error
    if (msg.includes('Too many requests')) {
      // Extract retry time if available (e.g., "in 1374 seconds")
      const match = msg.match(/in (\d+) seconds/);
      const retryTime = match ? Math.ceil(parseInt(match[1]) / 60) : null;
      const retryMsg = retryTime ? ` Please try again in ~${retryTime} minute${retryTime > 1 ? 's' : ''}.` : '';
      setError(`Rate limit exceeded.${retryMsg}`);
    }
    console.warn(`Failed to fetch ${context}:`, msg);
  };

  // Clean up stale WalletConnect cache on app initialization
  useEffect(() => {
    // Clear WalletConnect cache to prevent errors from incomplete sessions
    if (typeof window !== 'undefined' && window.localStorage) {
      const wcKeys = Object.keys(window.localStorage).filter(key =>
        key.startsWith('wc@2') || key.startsWith('walletconnect') || key.includes('wc_')
      );
      if (wcKeys.length > 0) {
        wcKeys.forEach(key => window.localStorage.removeItem(key));
      }
    }

    // Clean up expired rate limit data
    cleanupExpiredRateLimits();

    // Add global error handler to catch WalletConnect errors
    const handleError = (event) => {
      const errorMessage = event.error?.message || event.message || '';

      // Catch WalletConnect session cleanup errors (expected after cache clear)
      if (errorMessage.includes('session topic') ||
          errorMessage.includes('No matching key') ||
          errorMessage.includes('proposal:') ||
          errorMessage.includes('Proposal expired') ||
          errorMessage.includes('request reset')) {
        event.preventDefault();
        return true;
      }

      // Catch WalletConnect internal errors (setDefaultChain, request, etc.)
      // These occur during chain switching when provider state is not fully initialized
      if (errorMessage.includes('setDefaultChain') ||
          errorMessage.includes("Cannot read properties of undefined (reading 'request')") ||
          errorMessage.includes("Cannot read properties of undefined (reading 'setDefaultChain')")) {
        event.preventDefault();
        return true;
      }
    };

    // Handler for unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      const errorMessage = event.reason?.message || String(event.reason) || '';

      // Catch WalletConnect session cleanup errors (expected after cache clear)
      if (errorMessage.includes('session topic') ||
          errorMessage.includes('No matching key') ||
          errorMessage.includes('proposal:') ||
          errorMessage.includes('Proposal expired') ||
          errorMessage.includes('request reset')) {
        event.preventDefault();
        return true;
      }

      // Catch WalletConnect internal errors
      if (errorMessage.includes('setDefaultChain') ||
          errorMessage.includes("Cannot read properties of undefined (reading 'request')") ||
          errorMessage.includes("Cannot read properties of undefined (reading 'setDefaultChain')")) {
        event.preventDefault();
        return true;
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Sync URL params with state when navigating
  // Also reset address-related data to prevent stale data from previous address
  useEffect(() => {
    setCurrentChain(urlChain || null);
    setCurrentAddress(urlAddress || null);
    // Reset address data when URL changes to prevent showing stale data
    // Note: Don't reset matchedCoin here - let searchCoinByAddress effect handle it
    // to avoid race condition where addressData loads before matchedCoin query completes
    setAddressData(null);
    setLabelResult(null);
    setCurrentAddressAlias(null);
    setFundingSourcePrices(null);
    setFundingSourceSecurityLevels(null);
  }, [urlChain, urlAddress]);

  // Reset coin data when coinId changes to prevent showing stale data
  useEffect(() => {
    setCoin(null);
    setCoinHoldersData(null);
  }, [coinId]);

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [coinId, urlChain, urlAddress]);

  // Track homepage view (when no coin or address is being viewed)
  useEffect(() => {
    if (!coinId && !urlChain && !urlAddress) {
      trackPageView('homepage');
    }
  }, [coinId, urlChain, urlAddress]);


  // Fetch coin data when coinId is present
  // In hard gate mode, user must be authenticated
  // In soft gate mode, anyone can view
  useEffect(() => {
    // Use abort controller to prevent race conditions and double-fetching
    let isCancelled = false;

    async function fetchCoin() {
      if (!coinId || (HARD_GATE_MODE && !user)) {
        setCoin(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const coinDoc = await getDoc(doc(db, 'coins', coinId));

        if (isCancelled) return;

        if (coinDoc.exists()) {
          const coinData = coinDoc.data();

          // Normalize platform names
          for (const platform in (coinData?.platforms || {})) {
            const platforms = coinData.platforms;
            const newValue = {address: platforms[platform], isSupportedChain: true};
            try {
              const normalizedChain = normalizeChain(platform);
              if (normalizedChain !== platform) {
                platforms[normalizedChain] = newValue;
                delete platforms[platform];
              } else {
                platforms[platform] = newValue;
              }
            } catch(error) {
              newValue.isSupportedChain = false;
              platforms[platform] = newValue;
            }
          }

          // Set coin data immediately (without security level) to show UI faster
          // Security level will be set to 'loading' to show loading state
          setCoin({ id: coinDoc.id, ...coinData, securityLevel: 'loading' });
          setLoading(false);

          // Track coin page view
          trackPageView(`coin_${coinId}`);

          // Fetch security level and holders data asynchronously (lazy loading)
          // Run both in parallel for better performance
          const hasPlatforms = coinData.platforms && Object.keys(coinData.platforms).length > 0;

          // Fetch data in App.js instead of CoinInfo component to avoid data double-fetching issue 
          // caused by React Strict Mode (used in development) which intentionally unmounts and remounts components to help detect side effects.
          const securityPromise = getCoinSecurityLevel(coinId)
            .then(securityData => {
              if (isCancelled) return;
              // Backend returns platforms as a map: { "ethereum": 4, "polygon": 3, ... }
              setCoin(curCoin => curCoin ? {
                ...curCoin,
                securityLevel: securityData.securityLevel,
                securityDescription: securityData.securityDescription ?? null,
                platformSecurityLevels: securityData.platforms || {}
              } : null);
            })
            .catch(securityError => {
              if (isCancelled) return;
              handleLazyError(securityError, 'coin security level');
              setCoin(curCoin => curCoin ? {
                ...curCoin,
                securityLevel: null,
                securityDescription: null,
                platformSecurityLevels: {}
              } : null);
            });

          const holdersPromise = hasPlatforms
            ? getCoinHolders(coinId)
                .then(holdersData => {
                  if (isCancelled) return;
                  setCoinHoldersData(holdersData);
                })
                .catch(holdersError => {
                  if (isCancelled) return;
                  handleLazyError(holdersError, 'coin holders');
                  setCoinHoldersData({ error: true });
                })
            : Promise.resolve();

          await Promise.all([securityPromise, holdersPromise]);
        } else {
          setError(`We are sorry coin "${coinId}" is not supported yet. If you have a strong need for it, please send email to support@coincommons.io, we will try our best to support you. Thanks for your patience with us!`);
          setLoading(false);
          // Track page view even when coin not found (user still sees error page)
          trackPageView(`coin_${coinId}_not_found`);
        }
      } catch (err) {
        if (isCancelled) return;
        console.error('Error fetching coin:', err);
        // Check for rate limit error
        const msg = err.message || '';
        if (msg.includes('Too many requests')) {
          handleLazyError(err, 'coin data');
        } else {
          setError('Failed to load coin data');
        }
        setLoading(false);
        // Track page view even on error (user still sees error page)
        trackPageView(`coin_${coinId}_error`);
      }
    }

    fetchCoin();
    // - Added isCancelled flag with cleanup function to prevent double-fetching of getCoinSecurityLevel
    // - When React Strict Mode unmounts/remounts the component, the cleanup function sets isCancelled = true, preventing the first (cancelled) effect from updating
    // state
    return () => {
      isCancelled = true;
    };
  }, [coinId, user]);

  // Reset UI when currentAddress changes
  useEffect(() => {
    setShowAddAliasInput(false);
    setNewAliasValue('');
    setAliasError('');
  }, [currentAddress]);

  // Helper function to search for a coin matching an address on a specific chain
  // Returns the matched coin data or null if not found
  const findMatchingCoin = async (addressToSearch, chainToSearch) => {
    if (!addressToSearch || !chainToSearch) {
      return null;
    }

    const platform = CHAIN_TO_COINGECKO_PLATFORM[chainToSearch];
    if (!platform) {
      return null;
    }

    try {
      const coinsRef = collection(db, 'coins');
      const normalizedAddress = addressToSearch.toLowerCase();
      const q = query(coinsRef, where(`platforms.${platform}`, '==', normalizedAddress), limit(1));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }
      return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    } catch (error) {
      console.error('Error searching for coin:', error);
      return null;
    }
  };

  // Search for coin that matches current address on the current chain
  // Use urlAddress and urlChain directly to ensure effect runs when URL changes
  useEffect(() => {
    const searchCoinByAddress = async () => {
      const coin = await findMatchingCoin(urlAddress, urlChain);
      setMatchedCoin(coin);
    };
    searchCoinByAddress();
  }, [urlAddress, urlChain]);

  // Subscribe to current address alias with real-time updates
  useEffect(() => {
    // Use user.uid for both wallet and email users
    // (user.address is null for email users)
    if (!user?.uid || !currentAddress) {
      setCurrentAddressAlias(null);
      return;
    }

    // Subscribe to real-time updates from Firestore
    let isSubscribed = true;
    const unsubscribe = subscribeToAlias(null, currentAddress, (alias) => {
      // Only update if still subscribed (prevents stale updates after cleanup)
      if (isSubscribed) {
        setCurrentAddressAlias(alias);
      }
    }, currentChain || 'ethereum');

    // Cleanup subscription when component unmounts or dependencies change
    return () => {
      isSubscribed = false;
      unsubscribe();
    };
  }, [user?.uid, currentAddress, currentChain]);

  // Fetch watched addresses when user changes
  useEffect(() => {
    if (!user) {
      setWatchedAddresses([]);
      return;
    }

    const fetchWatched = async () => {
      const watched = await getWatchedAddresses();
      setWatchedAddresses(watched.map(addr => addr.toLowerCase()));
    };
    fetchWatched();
  }, [user]);

  // Listen for new AI comments to trigger security level refresh
  // When a new AI comment is created by the background task, re-fetch security level with forceRefresh
  useEffect(() => {
    if (!currentAddress || !currentChain) return;

    const normalizedAddress = currentAddress.toLowerCase();
    const q = query(
      collection(db, 'comments'),
      where('targetAddress', '==', normalizedAddress),
      where('authorAddress', '==', 'AI_Assistant'),
      where('removed', '==', false),  // Avoid AI generated comments of low quality that were later removed
      orderBy('updatedAt', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const comment = change.doc.data();
          const updatedAt = comment.updatedAt?.toMillis?.() || 0;
          const isRecent = Date.now() - updatedAt < 30000; // 30 seconds

          if (isRecent) {
            getAddressSecurityLevel(currentAddress, currentChain, { forceRefresh: true })
              .then(result => {
                setAddressData(prev => prev ? {
                  ...prev,
                  securityLevel: result.securityLevel ?? null,
                  securityDescription: result.securityDescription ?? null
                } : null);
              })
              .catch(error => {
                console.warn('Failed to refresh security level:', error.message);
              });
          }
        }
      });
    }, (error) => {
      console.warn('AI comment listener error:', error.message);
    });

    return () => unsubscribe();
  }, [currentAddress, currentChain, matchedCoin?.id]);

  // Listen for wallet account and chain changes
  useEffect(() => {
    // Listen for wallet account changes
    const unsubscribeAccounts = onAccountsChanged((newAddress) => {
      if (!newAddress) {
        // User disconnected wallet
        handleLogout();
      } else if (user && newAddress !== user.address) {
        // User switched accounts - log them out
        handleLogout();
        alert('Wallet address changed. Please connect again.');
      }
    });

    // Listen for chain changes
    const unsubscribeChain = onChainChanged((newChainId) => {
      if (user) {
        // Update chain ID in user state
        const updatedUser = { ...user, chainId: newChainId };
        setUser(updatedUser);
        localStorage.setItem('coincommons_user', JSON.stringify(updatedUser));
      }
    });

    // Cleanup function to remove event listeners
    return () => {
      if (unsubscribeAccounts) unsubscribeAccounts();
      if (unsubscribeChain) unsubscribeChain();
    };
  }, [user, handleLogout, setUser]);

  // Check URL for address on mount and when URL params change
  useEffect(() => {
    // Check for URL errors immediately (don't wait for auth)
    const { chain, address, error: urlError } = parseURLPath();

    if (urlError) {
      // Invalid URL (unsupported chain, invalid address) - show error immediately
      setError(urlError);
      setCurrentAddress(address);
      setCurrentChain(chain);
      return;
    }

    // Wait for auth to be initialized before proceeding with valid URLs
    if (!authInitialized) {
      return;
    }

    // Check if user is trying to access coin page without authentication (only in hard gate mode)
    if (HARD_GATE_MODE && coinId && !user) {
      setError('Please sign in to view coin details');
      setUrlBasedAuth(true);
      setShowAuthModal(true);
      return;
    }

    if (chain && address) {
      // In hard gate mode, require authentication to view address details
      if (HARD_GATE_MODE && !user) {
        setError('Please sign in to view address details');
        setUrlBasedAuth(true); // Mark that this auth request came from URL navigation
        setShowAuthModal(true);
        return;
      }
      // Skip if we're already viewing this address (avoid double-fetch from search selection)
      const normalizedAddress = normalizeAddress(address);
      if (addressData && addressData.address === normalizedAddress && addressData.chain === chain) {
        return;
      }
      // Load address data without updating URL (already in URL)
      setError(null);
      fetchAddressData(normalizedAddress, chain, 'url');
    }
    // urlChain and urlAddress are combined into a single key to avoid the "changing size" error
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authInitialized, coinId, `${urlChain || ''}_${urlAddress || ''}`]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // Re-check URL when browser back/forward buttons are used
      const { chain, address, error: urlError } = parseURLPath();

      if (urlError) {
        // Invalid URL (unsupported chain, invalid address) - show error immediately
        setError(urlError);
        setCurrentAddress(address);
        setCurrentChain(chain);
        return;
      }

      // Wait for auth to be initialized before proceeding with valid URLs
      if (!authInitialized) {
        return;
      }

      if (chain && address) {
        // In hard gate mode, require authentication to view address details
        if (HARD_GATE_MODE && !user) {
          setError('Please sign in to view address details');
          setUrlBasedAuth(true);
          setShowAuthModal(true);
          return;
        }
        // Load address data without updating URL (already in URL)
        setError(null);
        const normalizedAddress = normalizeAddress(address);
        fetchAddressData(normalizedAddress, chain, 'browser_navigation');
      } else {
        // Homepage - clear current address
        setCurrentAddress(null);
        setCurrentChain(null);
        setAddressData(null);
        setError(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authInitialized]);

  // Handle login - show auth modal
  const handleLogin = () => {
    setShowAuthModal(true);
  };

  // Wrapper for handleWalletConnectSuccess to close auth modal
  const handleWalletConnectSuccessWrapper = (result) => {
    handleWalletConnectSuccess(result);
    setShowAuthModal(false);
    setUrlBasedAuth(false); // Reset flag since user successfully authenticated
  };

  const handleProfileUpdate = () => {
    // Profile updates are automatically saved in ProfileModal
    // Just close the modal
  };

  const handleSaveCurrentAddressAlias = async () => {
    if (!user || !currentAddress) return;

    const trimmedValue = newAliasValue.trim();

    // Validate alias using shared validation
    const validation = validateAlias(trimmedValue);
    if (!validation.valid) {
      setAliasError(validation.error);
      return;
    }

    // Check rate limit before creating/updating alias
    const rateLimitCheck = checkRateLimit(user.uid, 'alias_create');
    if (!rateLimitCheck.allowed) {
      setAliasError(rateLimitCheck.message);
      return;
    }

    try {
      await setAddressAlias(user.uid, currentAddress, trimmedValue, currentChain);

      // Record action for rate limiting
      recordAction(user.uid, 'alias_create');

      // Track alias creation
      trackAliasCreate(currentChain);

      setCurrentAddressAlias(trimmedValue);
      setShowAddAliasInput(false);
      setNewAliasValue('');
      setAliasError('');
    } catch (err) {
      setAliasError(err.message);
    }
  };

  // Handle watching/unwatching the current address
  const handleWatchToggle = async () => {
    if (!user) {
      handleLogin();
      return;
    }
    if (!currentAddress) return;

    const isCurrentlyWatching = watchedAddresses.includes(currentAddress.toLowerCase());

    setWatchingAddress(true);
    setWatchError('');

    try {
      if (isCurrentlyWatching) {
        // Unwatch
        await removeWatchedAddress(currentAddress);
        setWatchedAddresses(prev => prev.filter(addr => addr !== currentAddress.toLowerCase()));
      } else {
        // Watch - validate first
        const validation = validateWatchAddress(currentAddress, watchedAddresses);
        if (!validation.valid) {
          setWatchError(validation.error);
          setTimeout(() => setWatchError(''), 5000);
          setWatchingAddress(false);
          return;
        }

        const result = await addWatchedAddress(currentAddress);
        if (result.success) {
          setWatchedAddresses(prev => [...prev, currentAddress.toLowerCase()]);
        } else {
          setWatchError(result.error);
          setTimeout(() => setWatchError(''), 5000);
        }
      }
    } catch (err) {
      console.error('Error toggling watch:', err);
      setWatchError(isCurrentlyWatching ? 'Failed to unwatch' : 'Failed to watch');
      setTimeout(() => setWatchError(''), 5000);
    } finally {
      setWatchingAddress(false);
    }
  };

  // Helper to get explorer info for current chain
  const getExplorerInfo = (chain, address) => {
    const url = getExplorerUrl(address, chain);
    const chainConfig = getChain(chain);

    if (!url || !chainConfig) {
      return { name: 'Explorer', url: '#' };
    }

    // Extract explorer name from URL (e.g., etherscan.io → Etherscan)
    const explorerNames = {
      ethereum: 'Etherscan',
      bitcoin: 'Blockchair',
      bitcoincash: 'Blockchair',
      litecoin: 'Blockchair',
    };

    return {
      name: explorerNames[chain] || 'Explorer',
      url: url
    };
  };

  // Fetch address blockchain API data based on chain type
  // address: normalized address (see normalizeAddress in addressUtils.js)
  const fetchAddressData = async (address, chain, searchSource = 'unknown') => {
    setError(null);

    // Get chain configuration
    const chainConfig = getChain(chain);

    // Validate address format
    const isValid = validateAddress(address, chain);
    if (!isValid) {
      const errorMessage = `Invalid ${chainConfig?.name || chain} address format. Please enter a valid address.`;
      setError(errorMessage);
      setLoading(false);

      // Don't track user side error
      return;
    }

    // Track address search with source info
    trackAddressSearch(chain, searchSource);

    try {
      // Handle all EVM chains using Moralis API via Cloud Function
      if (chainConfig && chainConfig.isEVM) {
        try {
          // OPTIMIZATION: Render page immediately with all loading states
          // All API calls are now lazy - no blocking waits
          const initialAddressData = {
            address,
            chain,
            // Balance - lazy loading
            balance: 'loading',
            balanceAmount: null,
            priceUSD: null,
            // Too heavy to calculate on the fly, set to 0 for now
            totalTransactions: 0,
            // Lazy loading fields
            firstSeen: 'loading',
            lastSeen: 'loading',
            fundingSources: 'loading',
            activityLevel: 'loading',
            avgTxsPerDay: null,
            netWorthData: 'loading',
            // Moralis doesn't provide sent/received breakdowns, set to 0
            totalReceived: 0,
            totalSent: 0,
            txSent1Day: 0,
            txReceived1Day: 0,
            txSent7Day: 0,
            txReceived7Day: 0,
            txSent30Day: 0,
            txReceived30Day: 0,
            // Contract status - lazy loading
            isContract: 'loading',
            // Security level - lazy loading
            securityLevel: 'loading',
            verifiedContract: null,
            // Token holder info - lazy loading
            totalHolders: 'loading',
            holdersByAcquisition: null            
          };

          // Render immediately - no waiting for any API calls
          setAddressData(initialAddressData);
          setCurrentAddress(address);
          setCurrentChain(chain);
          setLoading(false);

          // Track successful page view
          trackPageView(`${chain}_address_${address.slice(0, 10)}`);

          // ALL data fetched lazily in parallel - no blocking waits
          const lazyPromises = [];

          // 1. Balance + Price (was blocking before)
          lazyPromises.push(
            Promise.all([
              getPriceInUSD(chain),
              getEVMAddressData(address, chain)
            ])
              .then(([priceUSD, moralisData]) => {
                setAddressData(prevData => prevData ? {
                  ...prevData,
                  balance: moralisData.balanceFormatted + ' ' + (getChain(chain)?.symbol || 'ETH'),
                  balanceAmount: parseFloat(moralisData.balanceFormatted),
                  priceUSD,
                  netWorthData: moralisData.netWorthData || null
                } : null);

                // Return moralisData for dependent calls
                return moralisData;
              })
              .catch(error => {
                handleLazyError(error, 'balance/price');
                setAddressData(prevData => prevData ? {
                  ...prevData,
                  balance: 'N/A',
                  balanceAmount: 0,
                  priceUSD: 0,
                  netWorthData: null
                } : null);
                return null;
              })
          );

          // 2. Label (was blocking before)
          let firstLabelResult = null;
          lazyPromises.push(
            getLabel(address, chain)
              .then(labelResult => {
                firstLabelResult = labelResult;
                setLabelResult(labelResult);
              })
              .catch(error => {
                handleLazyError(error, 'label');
                setLabelResult(null);
              })
          );

          // 3. Contract status (determines whether to fetch net worth or token holders)
          lazyPromises.push(
            getAddressIsContract(address, chain)
              .then(async (contractData) => {
                const isContract = contractData.isContract;
                setAddressData(prevData => prevData ? {
                  ...prevData,
                  isContract
                } : null);

                // Fetch token holders for contracts
                if (isContract) {
                  try {
                    const holdersData = await getTokenHolders(address, chain);
                    setAddressData(prevData => prevData ? {
                      ...prevData,
                      totalHolders: holdersData.totalHolders,
                      holdersByAcquisition: holdersData.holdersByAcquisition
                    } : null);
                  } catch (error) {
                    console.warn('Failed to fetch token holders:', error.message);
                    setAddressData(prevData => prevData ? {
                      ...prevData,
                      totalHolders: null
                    } : null);
                  }
                } else {
                  // Set token holders to null for non-contracts
                  setAddressData(prevData => prevData ? {
                    ...prevData,
                    totalHolders: null
                  } : null);
                }
              })
              .catch(error => {
                handleLazyError(error, 'contract status');
                setAddressData(prevData => prevData ? {
                  ...prevData,
                  isContract: null,
                  totalHolders: null
                } : null);
              })
          );

          // 4. Transaction timestamps and funding sources
          lazyPromises.push(
            getAddressTimestamps(address, chain)
              .then(async timestampsData => {
                const firstSeen = timestampsData.firstTransaction ? formatRelativeTime(timestampsData.firstTransaction) : 'N/A';
                const lastSeen = timestampsData.lastTransaction ? formatRelativeTime(timestampsData.lastTransaction) : 'N/A';
                const fundingSources = timestampsData.fundingSources || [];

                setAddressData(prevData => prevData ? {
                  ...prevData,
                  firstSeen,
                  lastSeen,
                  fundingSources,
                  activityLevel: timestampsData.activityLevel,
                  avgTxsPerDay: timestampsData.avgTxsPerDay,
                  txCount: timestampsData.txCount,
                  txCountType: timestampsData.txCountType,
                  isNewAddress: timestampsData.isNewAddress,
                  isReceiveOnly: timestampsData.isReceiveOnly
                } : null);

                // Prepare data for parallel fetches
                const erc20Tokens = fundingSources
                  .filter(s => s.type !== 'native' && s.tokenAddress)
                  .map(s => ({ token: s.token, tokenAddress: s.tokenAddress }));

                const addressesToFetch = [];
                const securityMap = new Map();
                for (const source of fundingSources) {
                  if (source.type === 'pre-funded') continue;
                  // From address
                  if (source.address) {
                    const key = `from_${source.address.toLowerCase()}`;
                    if (!securityMap.has(key)) {
                      addressesToFetch.push({ address: source.address, chain, key });
                      securityMap.set(key, null);
                    }
                  }
                  // Token address (ERC20 tokens only, native tokens get level 5)
                  if (source.type === 'native' || source.type === 'nativeAirdrop') {
                    securityMap.set('token_native', 5);
                  } else if (source.tokenAddress) {
                    const key = `token_${source.tokenAddress.toLowerCase()}`;
                    if (!securityMap.has(key)) {
                      addressesToFetch.push({ address: source.tokenAddress, chain, key });
                      securityMap.set(key, null);
                    }
                  }
                }

                // Run token prices and security levels fetches in parallel
                await Promise.all([
                  // Fetch ERC20 token prices
                  erc20Tokens.length > 0
                    ? batchGetTokenPrices(erc20Tokens, chain)
                        .then(prices => setFundingSourcePrices(prices))
                        .catch(err => console.warn('Failed to batch fetch token prices:', err.message))
                    : Promise.resolve(),
                  // Fetch security levels for From addresses and ERC20 tokens
                  addressesToFetch.length > 0
                    ? getAddressSecurityLevels(addressesToFetch.map(a => ({ address: a.address, chain: a.chain })))
                        .then(results => {
                          results.forEach((result, index) => {
                            const key = addressesToFetch[index].key;
                            securityMap.set(key, result.securityLevel ?? null);
                          });
                          setFundingSourceSecurityLevels(securityMap);
                        })
                        .catch(err => console.warn('Failed to batch fetch security levels:', err.message))
                    : Promise.resolve(setFundingSourceSecurityLevels(securityMap))
                ]);
              })
              .catch(error => {
                handleLazyError(error, 'timestamps');
                setAddressData(prevData => prevData ? {
                  ...prevData,
                  firstSeen: 'N/A',
                  lastSeen: 'N/A',
                  fundingSources: []
                } : null);
              })
          );

          // 5. Security level for main address
          lazyPromises.push(
            getAddressSecurityLevel(address, chain)
              .then(securityData => {
                setAddressData(prevData => prevData ? {
                  ...prevData,
                  securityLevel: securityData.securityLevel ?? null,
                  securityDescription: securityData.securityDescription ?? null
                } : null);
              })
              .catch(error => {
                handleLazyError(error, 'security level');
                setAddressData(prevData => prevData ? {
                  ...prevData,
                  securityLevel: null,
                  securityDescription: null
                } : null);
              })
          );

          // Wait for all lazy data (don't block the UI)
          await Promise.allSettled(lazyPromises);

          // 6. Second label fetch - getAddressTimestamps and getEVMAddressData may have
          // harvested new label data, so fetch again to get fresh labels for target address
          try {
            const freshLabelResult = await getLabel(address, chain);
            // Only update if the label result actually changed (deep compare with sorted keys)
            const sortedStringify = (obj) => JSON.stringify(obj, Object.keys(obj || {}).sort());
            if (sortedStringify(freshLabelResult) !== sortedStringify(firstLabelResult)) {
              setLabelResult(freshLabelResult);
            }
          } catch (error) {
            console.warn('Second label fetch failed:', error.message);
            // Keep the first label result if second fetch fails
          }

          return;
        } catch (evmError) {
          console.error(`fetchAddressData error:`, evmError.message);
          // Track API error
          trackError('fetchAddressData', evmError.message, { chain, address});
          throw evmError;
        }
      } // EVM chains

      if (chain === 'bitcoin' || chain === 'bitcoincash') {
        try {
          // Clear label data for non-EVM chains
          setLabelResult(null);

          // Fetch Bitcoin/Bitcoin Cash data from Blockchain.com API
          const [priceUSD, btcData] = await Promise.all([
            getPriceInUSD(chain),
            getBitcoinAddressData(address, chain)
          ]);

          // Extract data from Blockchain.com response
          const balance = btcData.balance;
          const totalReceived = btcData.totalReceived;
          const totalSent = btcData.totalSent;
          const transactions = btcData.transactions || [];

          // Format balance
          const balanceInBTC = formatBitcoinBalance(balance);

          // Calculate transaction totals
          const totals = calculateBitcoinTransactionTotals(transactions, address, totalReceived, totalSent, chain);

          // Get first/last transaction times
          const firstTx = transactions.length > 0 ? transactions[transactions.length - 1] : null;
          const lastTx = transactions.length > 0 ? transactions[0] : null;

          const firstSeen = firstTx ? formatRelativeTime(firstTx.time * 1000) : 'N/A';
          const lastSeen = lastTx ? formatRelativeTime(lastTx.time * 1000) : 'N/A';

          // Get chain symbol
          const chainSymbol = getChain(chain)?.symbol || 'BTC';

          setAddressData({
            address,
            chain,
            balance: balanceInBTC + ' ' + chainSymbol,
            balanceAmount: parseFloat(balanceInBTC),
            priceUSD,
            netWorthUSD: parseFloat(balanceInBTC) * priceUSD,
            ...totals, // Spread all calculated totals
            firstSeen,
            lastSeen
          });
          setCurrentAddress(address);
          setCurrentChain(chain);
          setLoading(false);

          // Track successful page view
          trackPageView(`${chain}_address_${address.slice(0, 10)}`);
          return;
        } catch (btcError) {
          console.error(`${chain} API error:`, btcError.message);
          trackError('btc_api', btcError.message, { chain, address: address.slice(0, 10) });
          throw btcError;
        }
      } // bitcoin / bitcoincash

      throw new Error(`Unsupported chain: ${chain}`);
    } catch (err) {
      console.error(err.message);
      trackError('Address data error! Fallback to 0s.', err.message, { chain, address });

      setAddressData({
        address,
        chain,
        balance: '0.00000000 ' + (getChain(chain)?.symbol || '???'),
        balanceAmount: 0,
        priceUSD: 0,
        netWorthUSD: 0,
        txSent1Day: 0,
        txReceived1Day: 0,
        txSent7Days: 0,
        txReceived7Days: 0,
        txSent30Days: 0,
        txReceived30Days: 0,
        totalSent: 0,
        totalReceived: 0,
        totalTransactions: 0,
        firstSeen: 'N/A',
        lastSeen: 'N/A'
      });
      setCurrentAddress(address);
      setCurrentChain(chain);

      // Track page view even on error (user still sees the page with fallback data)
      trackPageView(`${chain}_address_${address.slice(0, 10)}`);
    } finally {
      setLoading(false);
    }
  };

  // Wrapper for handleSearch with auth check
  const handleSearch = async (searchTerm = null) => {
    // In hard gate mode, require authentication
    if (HARD_GATE_MODE && !user) {
      setShowAuthModal(true);
      return;
    }
    await performSearch(searchTerm);
  };

  const explorerInfo = addressData ? getExplorerInfo(addressData.chain, addressData.address) : null;

  // If profile modal is open, show only the profile page
  if (showProfileModal) {
    return (
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        userProfile={userProfile}
        setUser={setUser}
        onProfileUpdate={handleProfileUpdate}
      />
    );
  }

  // If Security FAQ is open, show only the FAQ page
  if (showSecurityFAQ) {
    return (
      <SecurityFAQPage
        onClose={() => setShowSecurityFAQ(false)}
      />
    );
  }

  // Generate SEO props based on current view
  const getSEOProps = () => {
    const baseUrl = 'https://coincommons.io';

    // Coin page SEO
    if (coinId && coin) {
      const coinName = coin.name || coinId;
      const coinSymbol = coin.symbol?.toUpperCase() || '';
      return {
        title: `${coinName} (${coinSymbol}) Safety Rating & User Reviews`,
        description: `Check ${coinName} (${coinSymbol}) safety rating, price, market cap, and community feedback. Make informed crypto decisions with CoinCommons.`,
        canonicalUrl: `${baseUrl}/coin/${coinId}`,
        ogType: 'article',
        ogImage: coin.image,
        structuredData: generateCoinStructuredData(coin)
      };
    }

    // Address page SEO
    if (currentAddress && addressData) {
      const chainName = getChain(currentChain)?.name || currentChain;
      const shortAddr = `${currentAddress.slice(0, 6)}...${currentAddress.slice(-4)}`;
      const { entityName, addressName } = formatAddressHumanReadable(
        matchedCoin, labelResult, currentAddressAlias, null
      );
      const displayName = entityName || addressName;
      const title = displayName
        ? `${displayName} - ${chainName} Address`
        : `${chainName} Address ${shortAddr}`;
      return {
        title,
        description: `View ${chainName} address ${shortAddr}${displayName ? ` (${displayName})` : ''}. Check safety rating, transaction history, and community comments on CoinCommons.`,
        canonicalUrl: `${baseUrl}/chain/${currentChain}/${currentAddress}`,
        ogType: 'article',
        structuredData: generateAddressStructuredData(addressData, chainName, currentAddress)
      };
    }

    // Homepage SEO
    return {
      title: null, // Use default
      description: 'Community-Powered Crypto Safety. Check crypto safety ratings, lookup wallet addresses, and read community reviews before you transact.',
      canonicalUrl: baseUrl,
      ogType: 'website',
      structuredData: generateWebsiteStructuredData()
    };
  };

  const seoProps = getSEOProps();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-100 to-gray-200">
      {/* SEO Meta Tags */}
      <SEO {...seoProps} />

      {/* Website Header */}
      <WebsiteHeader
        user={user}
        userOwnAlias={userOwnAlias}
        onLogoClick={() => {
          // Clear all state (navigation handled by Link)
          setCurrentAddress(null);
          setCurrentChain(null);
          setAddressData(null);
          setCoin(null);
          setError(null);
        }}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onProfileClick={() => setShowProfileModal(true)}
      />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Coin Page */}
        {coinId && coin ?
        <div className="space-y-6">
          <SearchBox
            searchAddress={searchAddress}
            setSearchAddress={setSearchAddress}
            searchResults={searchResults}
            showSearchResults={showSearchResults}
            setShowSearchResults={setShowSearchResults}
            searchLoading={searchLoading}
            loading={loading}
            onSearch={handleSearch}
          />
          <CoinInfo coin={coin} coinId={coinId} user={user} onLogin={handleLogin} holdersData={coinHoldersData} />
        {/* 
        I've added a loading state specifically for when a coin URL is accessed but the coin data hasn't loaded yet. The new logic:

        1. If coinId exists AND coin data is loaded (line 753): Show the coin page
        2. If coinId exists BUT coin is still null (line 768): Show a loading spinner with "Loading coin data..." message
        3. If no coinId and no currentAddress (line 771): Show homepage

        This prevents the flash by showing a loading state instead of the homepage while waiting for the coin data to load. The user will now see:
        - Direct loading spinner when accessing a coin URL
        - No homepage flash
        - Smooth transition to the coin page once data loads
        */}
        </div> : coinId && !coin && !error ? <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="animate-spin inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
          <p className="text-gray-600 text-lg">Loading coin data...</p>
        </div> : coinId && !coin && error ? <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="bg-red-50 border border-red-300 rounded-lg p-8 shadow-sm max-w-md">
            <div className="text-center">
              <Info className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Error</h3>
              <p className="text-red-700 mb-6">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setCoin(null);
                  navigate('/');
                }}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium"
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div> : !currentAddress ? <div className="flex flex-col items-center justify-center min-h-[60vh]">
          {/* Hero Section with Background */}
          <div className="text-center mb-8 w-full -mx-4 px-4 py-16 relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundImage: `linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.75) 100%), url('/hero-background.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundBlend: 'overlay'
          }}>
            <div className="absolute inset-0 bg-black opacity-10"></div>
            <div className="relative z-10">
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Community-Powered Crypto Safety</h2>
              <p className="text-white text-lg sm:text-xl mb-4 max-w-4xl mx-auto opacity-95">Check crypto safety ratings, lookup wallet addresses, and read community reviews before you transact.</p>
            </div>
          </div>

          {/* Core Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12 w-full max-w-4xl">
            {/* Feature 1: Search & Analyze */}
            <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm hover:shadow-md transition">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Search className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Search & Analyze</h3>
              </div>
              <p className="text-gray-600 text-sm">Investigate ~20k cryptocurrencies including Bitcoin, Ethereum, Official Trump etc and addresses from networks like Polygon, BSC etc.</p>
            </div>

            {/* Feature 2: Security Ratings */}
            <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm hover:shadow-md transition">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Safety Ratings</h3>
              </div>
              <p className="text-gray-600 text-sm">Check safety rating for cryptocurrencies and wallet addresses. Make safer transaction decisions with our risk assessments.</p>
            </div>

            {/* Feature: Bounties & Rewards - Coming Soon
            <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm hover:shadow-md transition">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Bounties & Rewards</h3>
              </div>
              <p className="text-gray-600 text-sm">Offer bounties for quality feedback on addresses. Contribute valuable insights to earn rewards. Creating a win-win community ecosystem.</p>
            </div>
            */}

            {/* Feature 3: Community Feedback */}
            <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm hover:shadow-md transition">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Community Feedback</h3>
              </div>
              <p className="text-gray-600 text-sm">Read and share experiences with addresses. Report scams and warn others in the community.</p>
            </div>

            {/* Feature 4: Get Notifications */}
            <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm hover:shadow-md transition">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-orange-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Bell className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Get Notifications</h3>
              </div>
              <p className="text-gray-600 text-sm">Stay updated on addresses you're watching. Get alerts when new activity or comments are posted.</p>
            </div>
          </div>

          {/* Popular Coins Link */}
          <div className="w-full max-w-4xl mb-8">
            <Link
              to="/popular-coins"
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 rounded-lg hover:from-purple-700 hover:to-pink-700 transition font-semibold shadow-lg flex items-center justify-center gap-3 text-lg"
            >
              <Award className="w-6 h-6" />
              View Popular Coins
            </Link>
          </div>

          {/* Call to Action / Search Box */}
          {(user || !HARD_GATE_MODE) ? (
            <div className="w-full max-w-4xl mb-8">
              <SearchBox
                searchAddress={searchAddress}
                setSearchAddress={setSearchAddress}
                searchResults={searchResults}
                showSearchResults={showSearchResults}
                setShowSearchResults={setShowSearchResults}
                searchLoading={searchLoading}
                loading={loading}
                onSearch={handleSearch}
              />
              <p className="text-gray-500 text-sm mt-2 text-center">Supports major cryptocurrencies across Ethereum and related networks like Polygon, Arbitrum, and more</p>
            </div>
          ) : (
            <div className="text-center mb-8">
              <button
                onClick={handleLogin}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-medium text-lg shadow-lg hover:shadow-xl"
              >
                <Wallet className="w-6 h-6" />
                Login to Start
              </button>
              <p className="text-gray-500 text-sm mt-4">Supports major cryptocurrencies across Ethereum and related networks like Polygon, Arbitrum, and more</p>
            </div>
          )}

          {error && <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-lg text-red-700 max-w-4xl text-center">{error}</div>}
        </div> : currentAddress && !addressData && !error ? <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="animate-spin inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
          <p className="text-gray-600 text-lg">Loading address data...</p>
        </div> : currentAddress && !addressData && error ? <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="bg-red-50 border border-red-300 rounded-lg p-8 shadow-sm max-w-md">
            <div className="text-center">
              <Info className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Error</h3>
              <p className="text-red-700 mb-6">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setCurrentAddress(null);
                  setCurrentChain(null);
                  setAddressData(null);
                  navigate('/');
                }}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium"
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div> : (HARD_GATE_MODE && !user) ? <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="bg-white border border-gray-300 rounded-lg p-8 shadow-sm max-w-md">
            <div className="text-center mb-6">
              <Wallet className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Sign In Required</h3>
              <p className="text-gray-600 mb-6">Please sign in to view address details and reports</p>
              <button onClick={handleLogin} className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-medium">
                <Wallet className="w-5 h-5" />
                Login
              </button>
            </div>
          </div>
        </div> : <div className="space-y-6">
          <SearchBox
            searchAddress={searchAddress}
            setSearchAddress={setSearchAddress}
            searchResults={searchResults}
            showSearchResults={showSearchResults}
            setShowSearchResults={setShowSearchResults}
            searchLoading={searchLoading}
            loading={loading}
            onSearch={handleSearch}
          />
          {error && <div className="mt-3 p-3 bg-red-50 border border-red-300 rounded text-red-700 text-sm">{error}</div>}
          <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
            {/* Section Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <Wallet className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <h3 className="text-xl font-bold text-gray-800">Address Information</h3>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Organization Verification Badge (compact, for header) */}
                {/* <OrgVerificationBadge
                  address={addressData.address}
                  variant="compact"
                  chain={currentChain ? getChain(currentChain)?.id || currentChain : 'ethereum'}
                  showRequestButton={false}
                /> */}
                {/* User Achievement Badges */}
                <UserBadges
                  address={addressData.address}
                  variant="compact"
                  chain={currentChain ? getChain(currentChain)?.id || currentChain : 'ethereum'}
                />
              </div>
            </div>

            {/* Hero Header - Identity Display (like CoinInfo) */}
            {(() => {
              const {
                entityName, entityNameSource: entityType,
                symbolName, logo,
                addressName, addressNameSource: addressType,
              } = formatAddressHumanReadable(
                matchedCoin,
                labelResult,
                currentAddressAlias,
                null
              );

              return (
            <div className="flex justify-center mb-6">
              {/* Logo + Names as one centered block */}
              <div className={`flex ${logo ? 'items-center gap-4' : 'flex-col items-center'}`}>
                {/* Logo/Avatar - show if available from org, coin, or token */}
                {logo && (
                  <img
                    src={logo}
                    alt="Identity"
                    className="w-16 h-16 rounded-full flex-shrink-0"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <div className={logo ? '' : 'text-center'}>
                {/* Identity Name */}
                {(() => {

                  const hasIdentity = entityName || addressName;

                  if (hasIdentity) {
                    const priceChange = matchedCoin?.price_change_percentage_24h;
                    const isPriceUp = priceChange >= 0;

                    return (
                      <>
                        <div className={`flex items-center gap-2 flex-wrap mb-1 ${logo ? '' : 'justify-center'}`}>
                          {/* Entity name */}
                          {entityName && (
                            <Tooltip text={entityType}>
                              <h2 className="text-2xl font-bold text-gray-900 break-words max-w-full cursor-help">{entityName}</h2>
                            </Tooltip>
                          )}
                          {symbolName && (
                            <Tooltip text='Symbol'>
                              <span className="text-lg text-gray-500 uppercase font-mono break-all max-w-full">
                                {symbolName}
                              </span>
                            </Tooltip>
                          )}
                          {matchedCoin?.market_cap_rank && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold whitespace-nowrap">
                              Rank #{matchedCoin.market_cap_rank}
                            </span>
                          )}
                          {/* Separator when both exist */}
                          {entityName && addressName && (
                            <span className="text-gray-400 text-xl">•</span>
                          )}
                          {/* Address name */}
                          {addressName && (
                            <Tooltip text={addressType}>
                              <span className={`${entityName ? 'text-lg text-gray-600' : 'text-2xl font-bold text-gray-900'} break-words max-w-full cursor-help`}>
                                {addressName}
                              </span>
                            </Tooltip>
                          )}
                          {/* Edit button when address identity is from private alias */}
                          {user && currentAddressAlias && !showAddAliasInput && (
                            <button
                              onClick={() => {
                                setNewAliasValue(currentAddressAlias);
                                setShowAddAliasInput(true);
                              }}
                              className="flex items-center gap-1 px-2 py-0.5 text-xs text-blue-600 hover:text-blue-800 hover:bg-gray-100 rounded transition"
                              title="Edit alias"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                          )}
                          
                        </div>
                        {/* Price & Change for matched coins */}
                        {matchedCoin?.current_price != null && (
                          <div className={`flex items-baseline gap-3 flex-wrap mb-2 ${logo ? '' : 'justify-center'}`}>
                            <Tooltip text='Current price'>
                              <div className="text-xl font-bold text-gray-900">
                                ${matchedCoin.current_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: matchedCoin.current_price < 1 ? 6 : 2 })}
                              </div>
                            </Tooltip>
                            {priceChange != null && (
                              <Tooltip text='Price change percentage over the last 24 hours'>
                                <div className={`flex items-center gap-1 font-semibold whitespace-nowrap ${isPriceUp ? 'text-green-600' : 'text-red-600'}`}>
                                  {isPriceUp ? <TrendingUp className="w-4 h-4 flex-shrink-0" /> : <TrendingDown className="w-4 h-4 flex-shrink-0" />}
                                  <span>{isPriceUp ? '+' : ''}{priceChange.toFixed(2)}%</span>
                                  <span className="text-xs text-gray-500 font-normal ml-1">(24h)</span>
                                </div>
                              </Tooltip>
                            )}
                            {matchedCoin?.market_cap && (
                              <Tooltip text='Market capitalization'>
                                <span className="text-sm text-gray-500">
                                  MCap: {matchedCoin.market_cap >= 1e12 ? `$${(matchedCoin.market_cap / 1e12).toFixed(2)}T` :
                                        matchedCoin.market_cap >= 1e9 ? `$${(matchedCoin.market_cap / 1e9).toFixed(2)}B` :
                                        matchedCoin.market_cap >= 1e6 ? `$${(matchedCoin.market_cap / 1e6).toFixed(2)}M` :
                                        `$${matchedCoin.market_cap.toLocaleString()}`}
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </>
                    );
                  }
                  // No identity name - show "+ Add Private Alias" (prompts login if not logged in)
                  if (!showAddAliasInput) {
                    return (
                      <div className="mb-1">
                        <button
                          onClick={() => {
                            if (user) {
                              setShowAddAliasInput(true);
                            } else {
                              handleLogin();
                            }
                          }}
                          className="text-2xl font-bold text-blue-600 hover:text-blue-700 transition"
                        >
                          + Add Your Private Alias for This Address
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* (You) indicator and Watch button */}
                {(() => {
                  const isYou = user?.address?.toLowerCase() === addressData.address.toLowerCase();
                  const isWatched = watchedAddresses.includes(addressData.address.toLowerCase());
                  return (
                    <div className={`flex items-center gap-3 ${logo ? '' : 'justify-center'}`}>
                      {isYou && (
                        <span className="text-lg font-semibold text-green-600">(You)</span>
                      )}
                      <WatchButton
                        isWatching={isWatched}
                        loading={watchingAddress}
                        onClick={handleWatchToggle}
                        error={watchError}
                      />
                    </div>
                  );
                })()}

                {/* Subtitle: Chain + Address Type + Explorer Link */}
                <div className={`flex items-center gap-2 flex-wrap mb-2 ${logo ? '' : 'justify-center'}`}>
                  <ChainBadge chain={addressData.chain} size="sm" />
                  <Tooltip text="Smart Contract: A programmable account containing code that executes automatically. EOA (Externally Owned Account): A regular wallet controlled by a private key.">
                    <span className="text-sm text-gray-600 flex items-center gap-1">
                      {addressData.isContract === 'loading' ? (
                        <>
                          <span className="inline-block w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                        </>
                      ) : (
                        <>
                          {addressData.isContract === false && '👤 EOA'}
                          {addressData.isContract === true && addressData.verifiedContract === false && <span className="text-red-600">📜 Unverified Contract</span>}
                          {addressData.isContract === true && addressData.verifiedContract !== false && '📜 Smart Contract'}
                          {addressData.isContract == null && ''}
                        </>
                      )}
                    </span>
                  </Tooltip>
                  {matchedCoin && (
                    <>
                      <span className="text-gray-400">•</span>
                      <Link
                        to={`/coin/${matchedCoin.id}`}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 transition"
                      >
                        View Coin Details<ExternalLink className="w-3 h-3" />
                      </Link>
                    </>
                  )}
                </div>

                {/* Address (smaller, secondary) */}
                <div className={`flex items-center gap-1 ${logo ? '' : 'justify-center'}`}>
                  {/* Full address on larger screens, shortened on mobile */}
                  <code className="hidden sm:inline text-gray-600 font-mono text-sm">
                    {addressData.address}
                  </code>
                  <code className="sm:hidden text-gray-600 font-mono text-sm">
                    {formatAddress(addressData.address)}
                  </code>
                  <CopyButton text={addressData.address} label="address" />
                </div>
                </div>
              </div>
            </div>
              );
            })()}

            {/* Alias input form - outside hero block to maintain centering */}
            {user && showAddAliasInput && (
              <div className="flex justify-center mb-6">
                <div className="space-y-2 w-64">
                  <SingleLineInput
                    value={newAliasValue}
                    onChange={setNewAliasValue}
                    onSave={handleSaveCurrentAddressAlias}
                    onCancel={() => {
                      setShowAddAliasInput(false);
                      setNewAliasValue('');
                      setAliasError('');
                    }}
                    maxLength={ALIAS_MAX_LENGTH}
                    error={aliasError}
                    placeholder="Enter alias..."
                    size="small"
                  />
                </div>
              </div>
            )}

            {/* Safety Level */}
            <SafetyLevel level={addressData.securityLevel} description={addressData.securityDescription} centered />

            {/* Token Holders Section - Only for contracts, using shared component */}
            {addressData.isContract === true && addressData.totalHolders != null && (
              <TokenHolders
                totalHolders={addressData.totalHolders}
                holdersByAcquisition={addressData.holdersByAcquisition}
              />
            )}

            {/* Stats Grid for EOA - matching CoinInfo market stats style */}
            {addressData.isContract !== true && addressData.isContract !== 'loading' && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-2 pb-6 border-b border-gray-200">
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-1">Current Balance</p>
                  <p className="text-base font-semibold text-gray-900 flex items-center gap-2 justify-center">
                    {addressData.balance === 'loading' ? (
                      <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      addressData.balance || 'N/A'
                    )}
                  </p>
                  {addressData.priceUSD && addressData.balanceAmount && (
                    <p className="text-xs text-gray-500">≈ ${(addressData.balanceAmount * addressData.priceUSD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-1">Net Worth</p>
                  <p className="text-base font-semibold text-gray-900 flex items-center gap-2 justify-center">
                    {addressData.netWorthData === 'loading' ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                      </>
                    ) : addressData.netWorthData?.netWorthUsd != null ? (
                      `$${addressData.netWorthData.netWorthUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${addressData.netWorthData.truncated ? '+' : ''}`
                    ) : (
                      'N/A'
                    )}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-1">First Seen</p>
                  <p className="text-base font-semibold text-gray-900 flex items-center gap-2 justify-center">
                    {addressData.firstSeen === 'loading' ? (
                      <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      addressData.firstSeen || 'N/A'
                    )}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-1">Last Seen</p>
                  <p className="text-base font-semibold text-gray-900 flex items-center gap-2 justify-center">
                    {addressData.lastSeen === 'loading' ? (
                      <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      addressData.lastSeen || 'N/A'
                    )}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-1">Activity</p>
                  {addressData.activityLevel === 'loading' ? (
                    <p className="text-base font-semibold flex items-center gap-2 justify-center">
                      <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                    </p>
                  ) : addressData.isNewAddress ? (
                    <>
                      <p className="text-base font-semibold text-gray-900 flex items-center gap-2 justify-center">New Address</p>
                      {addressData.txCount != null && addressData.txCount > 0 && (
                        <p className="text-xs text-gray-500">
                          {addressData.txCountType === 1 ? '~' : ''}{addressData.txCount.toLocaleString()} tx{addressData.txCount !== 1 ? 's' : ''}
                        </p>
                      )}
                    </>
                  ) : (addressData.activityLevel === 0 || addressData.activityLevel == null) ? (
                    <p className="text-base font-semibold text-gray-400">N/A</p>
                  ) : (
                    <>
                      <p className="text-base font-semibold flex items-center gap-2 justify-center">
                        {addressData.activityLevel === 1 ? (
                          <span className="text-green-600">Low</span>
                        ) : addressData.activityLevel === 2 ? (
                          <span className="text-blue-600">Medium</span>
                        ) : addressData.activityLevel === 3 ? (
                          <span className="text-yellow-600">High</span>
                        ) : addressData.activityLevel === 4 ? (
                          <span className="text-orange-600">Very High</span>
                        ) : (
                          <span className="text-red-600">Extreme</span>
                        )}
                      </p>
                      {addressData.txCount != null && addressData.txCount > 0 && (
                        <p className="text-xs text-gray-500">
                          {addressData.txCountType === 1 ? '~' : ''}{addressData.txCount.toLocaleString()} tx{addressData.txCount !== 1 ? 's' : ''}
                          {/* Only show rate for approximated counts (txCountType === 1) */}
                          {addressData.txCountType === 1 && addressData.avgTxsPerDay != null && (
                            <>
                              , ~{addressData.avgTxsPerDay >= 1 ? (
                                <>{Math.round(addressData.avgTxsPerDay).toLocaleString()} txs/day</>
                              ) : addressData.avgTxsPerDay * 30 >= 1 ? (
                                <>{Math.round(addressData.avgTxsPerDay * 30).toLocaleString()} txs/month</>
                              ) : (
                                <>{Math.round(addressData.avgTxsPerDay * 365).toLocaleString()} txs/year</>
                              )}
                            </>
                          )}
                        </p>
                      )}
                      <p className="text-xs text-gray-400">
                        {addressData.activityLevel === 1 ? (
                          addressData.isReceiveOnly ? 'Receive only' : 'Normal user'
                        ) : addressData.activityLevel === 2 ? (
                          'Active user'
                        ) : addressData.activityLevel === 3 ? (
                          'Very active trader'
                        ) : addressData.activityLevel === 4 ? (
                          'Bot or automated'
                        ) : (
                          'High-volume bot'
                        )}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Stats Grid for Contracts - First Seen / Last Seen */}
            {addressData.isContract === true && (
              <div className="grid grid-cols-2 gap-4 mb-2 pb-6 border-b border-gray-200">
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-1">First Seen</p>
                  <p className="text-base font-semibold text-gray-900 flex items-center gap-2 justify-center">
                    {addressData.firstSeen === 'loading' ? (
                      <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      addressData.firstSeen || 'N/A'
                    )}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-1">Last Seen</p>
                  <p className="text-base font-semibold text-gray-900 flex items-center gap-2 justify-center">
                    {addressData.lastSeen === 'loading' ? (
                      <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      addressData.lastSeen || 'N/A'
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Funded By Section - show all funding sources in a table */}
            {addressData.fundingSources === 'loading' ? (
              <div className="text-center mb-4">
                <p className="text-sm font-semibold text-gray-700 mb-2 cursor-help" title="Shows the first incoming transactions that funded this address (per coin, max 10)">Funded By</p>
                <p className="text-base font-semibold text-gray-900 flex items-center gap-2 justify-center">
                  <span className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                </p>
              </div>
            ) : addressData.fundingSources && addressData.fundingSources.length > 0 ? (
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-700 mb-2 text-center cursor-help" title="Shows the first incoming transactions that funded this address (per coin, max 10)">Funded By</p>
                <div className="overflow-x-auto flex justify-center">
                  <table className="text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 border-b">
                        <th className="pb-1 pr-3 text-left font-normal">Time</th>
                        <th className="pb-1 pr-3 text-left font-normal">From</th>
                        <th className="pb-1 pr-1 text-right font-normal">Amount</th>
                        <th className="pb-1 pr-3 text-left font-normal pl-1">Coin</th>
                        <th className="pb-1 text-right font-normal">USD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {addressData.fundingSources.map((source, index) => {
                        // Special handling for pre-funded (L1→L2 bridge deposit)
                        if (source.type === 'pre-funded') {
                          const chainConfig = getChain(currentChain);
                          const nativeSymbol = (chainConfig?.symbol || 'ETH').toUpperCase();
                          const coinId = getNativeTokenCoinId(nativeSymbol);
                          const nativeColors = getSecurityLevelInfo(5); // Native tokens are level 5
                          return (
                            <tr key={index} className="border-b border-gray-100 last:border-0">
                              <td className="py-1.5 pr-3 text-gray-400 text-xs whitespace-nowrap" title="Exact time unknown - deposit happened before first on-chain activity">
                                -
                              </td>
                              <td className="py-1.5 pr-3">
                                <span className="text-gray-600" title="Address was funded via L1→L2 bridge before any on-chain activity">
                                  Pre-funded
                                </span>
                              </td>
                              <td className="py-1.5 pr-1 text-right font-medium whitespace-nowrap">
                                <span className="font-mono text-gray-400">-</span>
                              </td>
                              <td className="py-1.5 pr-3 text-left pl-1 max-w-[120px]">
                                {coinId ? (
                                  <Link to={`/coin/${coinId}`} className="hover:underline">
                                    <span className={`block truncate px-1 rounded ${nativeColors.bg} ${nativeColors.textColor}`}>{nativeSymbol}</span>
                                  </Link>
                                ) : (
                                  <span className={`block truncate px-1 rounded ${nativeColors.bg} ${nativeColors.textColor}`}>{nativeSymbol}</span>
                                )}
                              </td>
                              <td className="py-1.5 text-right text-gray-400">-</td>
                            </tr>
                          );
                        }

                        const amount = parseFloat(source.amount) || 0;
                        const formattedAmount = amount > 0 ?
                          amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 4 }) : '0';
                        const displayName = source.label || source.entity;
                        const truncatedName = displayName && displayName.length > 18
                          ? displayName.substring(0, 16) + '...'
                          : displayName;
                        const shortAddress = formatAddress(source.address);

                        return (
                          <tr key={index} className="border-b border-gray-100 last:border-0">
                            <td className="py-1.5 pr-3 text-gray-500 text-xs whitespace-nowrap">
                              {formatRelativeTime(new Date(source.timestamp).getTime())}
                            </td>
                            <td className="py-1.5 pr-3">
                              {(() => {
                                const fromSecurityLevel = fundingSourceSecurityLevels?.get(`from_${source.address.toLowerCase()}`);
                                const fromColors = getSecurityLevelInfo(fromSecurityLevel);
                                return (
                                  <div className="flex items-center gap-1">
                                    <Link
                                      to={`/chain/${currentChain}/${source.address}`}
                                      className="truncate hover:underline"
                                      title={source.address}
                                    >
                                      {/* Wide screen: "Label (0xaaaa...aaaa)" or just address */}
                                      <span className={`hidden md:inline px-1 rounded ${fromColors.bg} ${fromColors.textColor}`}>
                                        {displayName && displayName.length <= 20 ? (
                                          <>{displayName} (<span className="font-mono">{shortAddress}</span>)</>
                                        ) : displayName ? (
                                          displayName
                                        ) : (
                                          <span className="font-mono">{shortAddress}</span>
                                        )}
                                      </span>
                                      {/* Medium screen: label or address */}
                                      <span className={`hidden sm:inline md:hidden px-1 rounded ${fromColors.bg} ${fromColors.textColor}`}>
                                        {displayName || <span className="font-mono">{shortAddress}</span>}
                                      </span>
                                      {/* Small screen: truncated label or address */}
                                      <span className={`sm:hidden px-1 rounded ${fromColors.bg} ${fromColors.textColor}`}>
                                        {truncatedName || <span className="font-mono">{shortAddress}</span>}
                                      </span>
                                    </Link>
                                    <CopyButton text={source.address} label="address" />
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="py-1.5 pr-1 text-right font-medium whitespace-nowrap">
                              <span className="font-mono">{formattedAmount}</span>
                            </td>
                            <td className="py-1.5 pr-3 text-left pl-1 max-w-[120px]">
                              {(() => {
                                let tokenLink = null;
                                if (source.type === 'native' || source.type === 'nativeAirdrop') {
                                  const coinId = getNativeTokenCoinId(source.token);
                                  if (coinId) {
                                    tokenLink = `/coin/${coinId}`;
                                  }
                                  // No link for unknown native tokens
                                } else if (source.tokenAddress) {
                                  tokenLink = `/chain/${currentChain}/${source.tokenAddress}`;
                                }

                                let tokenTooltip = '';
                                if (source.tokenName && source.token) {
                                  tokenTooltip = `${source.tokenName} (${source.token})`;
                                } else {
                                  tokenTooltip = source.tokenName || source.token || '';
                                }

                                // Get security level for token
                                const isNative = source.type === 'native' || source.type === 'nativeAirdrop';
                                const tokenSecurityLevel = isNative
                                  ? fundingSourceSecurityLevels?.get('token_native')
                                  : fundingSourceSecurityLevels?.get(`token_${source.tokenAddress?.toLowerCase()}`);
                                const tokenColors = getSecurityLevelInfo(tokenSecurityLevel);

                                const content = (
                                  <span
                                    className={`block truncate px-1 rounded ${tokenColors.bg} ${tokenColors.textColor}`}
                                    title={tokenTooltip}
                                  >
                                    {source.token?.toUpperCase() || source.token}
                                  </span>
                                );

                                return tokenLink ? (
                                  <Link to={tokenLink} className="hover:underline">
                                    {content}
                                  </Link>
                                ) : content;
                              })()}
                            </td>
                            <td className="py-1.5 text-right text-gray-500">
                              <FundingSourceUSD source={source} nativePrice={addressData.priceUSD} tokenPrices={fundingSourcePrices} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {fundingSourceSecurityLevels && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-600">
                    <span>Very Low Safety</span>
                    <div className="w-32 h-2 rounded-full bg-gradient-to-r from-red-400 via-yellow-300 to-green-400"></div>
                    <span>Excellent Safety</span>
                  </div>
                )}
              </div>
            ) : null}

            {/* External Explorer Link */}
            <div className="mt-2 text-center">
              <a href={explorerInfo.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 transition">
                View on {explorerInfo.name}<ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
          <CommentSection
            targetAddress={currentAddress}
            targetChain={currentChain}
            user={user}
            onLogin={handleLogin}
          />
        </div>}
      </div>

      {/* Footer */}
      <WebsiteFooter
        onSecurityFAQClick={() => setShowSecurityFAQ(true)}
      />

      {/* Wallet Connect Modal */}
      <WalletConnectModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          // If user cancelled auth from URL navigation (hard gate), go back to previous page
          if (urlBasedAuth) {
            setUrlBasedAuth(false);
            setCurrentAddress(null);
            setCurrentChain(null);
            setAddressData(null);
            setCoin(null);
            setError(null);
            navigate(-1); // Go back to previous page (e.g., popular coins)
          } else if (addressData || coin) {
            // User is viewing content and triggered sign-in (e.g., Watch button)
            // Just close modal, stay on current page
          } else {
            // User on homepage, just close modal
            setError(null);
          }
        }}
        onSuccess={handleWalletConnectSuccessWrapper}
      />
    </div>
  );
};

// Loading fallback for lazy-loaded components
const LoadingFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-100 to-gray-200 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Wrapper component with Router
const App = () => {
  return (
    <Router>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Email auth callback route */}
          <Route path="/auth/email-callback" element={<EmailAuthCallback />} />
          {/* Home page, address pages, and coin pages */}
          <Route path="/" element={<CoinCommons />} />
          <Route path="/chain/:chain/:address" element={<CoinCommons />} />
          <Route path="/coin/:coinId" element={<CoinCommons />} />
          <Route path="/popular-coins" element={<PopularCoins />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;